import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box, useTheme, useMediaQuery } from '@mui/material';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface Dataset {
  label: string;
  data: number[];
  borderColor: string;
  backgroundColor?: string;
  fill?: boolean;
  tension?: number;
}

interface LineChartProps {
  labels: string[];
  datasets: Dataset[];
  height?: number;
  showLegend?: boolean;
  showGrid?: boolean;
}

export const LineChart: React.FC<LineChartProps> = ({
  labels,
  datasets,
  height = 300,
  showLegend = true,
  showGrid = true
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: false,
        grid: {
          display: showGrid,
          color: theme.palette.divider,
          drawBorder: false
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            family: theme.typography.fontFamily,
            size: isMobile ? 9 : 11
          },
          maxTicksLimit: isMobile ? 5 : 8
        }
      },
      x: {
        grid: {
          display: showGrid,
          color: theme.palette.divider,
          drawBorder: false
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            family: theme.typography.fontFamily,
            size: isMobile ? 9 : 11
          },
          maxRotation: isMobile ? 45 : 0,
          maxTicksLimit: isMobile ? 6 : 10
        }
      }
    },
    plugins: {
      legend: {
        display: showLegend,
        position: 'top' as const,
        labels: {
          color: theme.palette.text.primary,
          font: {
            family: theme.typography.fontFamily,
            size: isMobile ? 10 : 12
          },
          usePointStyle: true,
          boxWidth: isMobile ? 6 : 10,
          padding: isMobile ? 10 : 20
        }
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(0, 0, 0, 0.8)' 
          : 'rgba(255, 255, 255, 0.9)',
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: isMobile ? 6 : 10,
        boxPadding: isMobile ? 3 : 5,
        cornerRadius: 4,
        bodyFont: {
          family: theme.typography.fontFamily,
          size: isMobile ? 10 : 12
        },
        titleFont: {
          family: theme.typography.fontFamily,
          weight: 'bold' as const,
          size: isMobile ? 11 : 13
        }
      }
    },
    elements: {
      line: {
        tension: 0.3
      },
      point: {
        radius: isMobile ? 2 : 3,
        hoverRadius: isMobile ? 3 : 5,
        hitRadius: isMobile ? 20 : 30
      }
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false
    }
  };

  const data = {
    labels,
    datasets: datasets.map(dataset => ({
      ...dataset,
      tension: dataset.tension || 0.3,
      pointBackgroundColor: dataset.borderColor,
      pointBorderColor: theme.palette.background.paper,
      pointBorderWidth: 1.5,
      fill: dataset.fill || false,
      backgroundColor: dataset.backgroundColor || (dataset.fill 
        ? `${dataset.borderColor}20` // 20 is hex for 12% opacity
        : undefined)
    }))
  };

  return (
    <Box sx={{ height: `${height}px`, width: '100%' }}>
      <Line options={options} data={data} />
    </Box>
  );
};
